'use client';

import { useEffect, useRef } from 'react';
import type { GoonDeckHardMaskConfig } from '@/lib/goondeck-foil-presets';
import { drawHardMask, prepareHardMaskSource, preparePatternAlpha, type HardMaskSourceCache } from '@/lib/goondeck-hard-mask';

const CANVAS_W = 300;
const CANVAS_H = 450;

export interface HardMaskCanvasProps {
  artUrl: string;
  config: GoonDeckHardMaskConfig;
  glareX: number;
  glareY: number;
  tiltAngle: number;
  tiltDistance: number;
  /** The card's current pattern mesh (facet/dots/stars/grain/rain/glitch/
   * glisten), same SVG data: URL the CSS mask-image uses — only needed when
   * config.target === 'pattern', which restricts the hard mask to wherever
   * this mesh actually shows instead of a fixed geometric region. */
  patternMeshUrl?: string | null;
}

/** Real per-pixel edge/luminance detection off the card's actual artwork —
 * ONLY ever render this in the single-card inspect view. It draws the
 * image to an offscreen canvas once (cheap, on artUrl change) but re-walks
 * every pixel on each pointer move (not cheap) — fine for one card
 * reacting to one cursor, would not be fine mounted per-card in a grid. */
export function HardMaskCanvas({ artUrl, config, glareX, glareY, tiltAngle, tiltDistance, patternMeshUrl }: HardMaskCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const cacheRef = useRef<HardMaskSourceCache | null>(null);
  const patternAlphaRef = useRef<Float32Array | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (config.mode === 'off') return;
    cacheRef.current = null;
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.src = artUrl;
    img.onload = () => {
      cacheRef.current = prepareHardMaskSource(img, CANVAS_W, CANVAS_H);
    };
    imageRef.current = img;
  }, [artUrl, config.mode]);

  // Loaded independently from the art above — only actually needed when the
  // target is "pattern", but cheap enough to keep ready any time a mesh is
  // assigned so switching target doesn't need to wait on a fresh load.
  useEffect(() => {
    if (config.mode === 'off' || !patternMeshUrl) {
      patternAlphaRef.current = null;
      return;
    }
    const img = new window.Image();
    img.src = patternMeshUrl;
    img.onload = () => {
      patternAlphaRef.current = preparePatternAlpha(img, CANVAS_W, CANVAS_H);
    };
  }, [patternMeshUrl, config.mode]);

  useEffect(() => {
    if (config.mode === 'off') {
      const ctx = canvasRef.current?.getContext('2d');
      ctx?.clearRect(0, 0, CANVAS_W, CANVAS_H);
      return;
    }
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const canvas = canvasRef.current;
      const cache = cacheRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx || !cache) return;
      cache.patternAlpha = patternAlphaRef.current ?? undefined;
      drawHardMask(ctx, cache, config, glareX, glareY, tiltAngle, tiltDistance);
    });
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [config, glareX, glareY, tiltAngle, tiltDistance]);

  if (config.mode === 'off') return null;

  return (
    <canvas
      ref={canvasRef}
      width={CANVAS_W}
      height={CANVAS_H}
      className="pointer-events-none absolute inset-0 h-full w-full"
      style={{ mixBlendMode: 'screen' }}
      aria-hidden="true"
    />
  );
}
